import { buttonVariants } from "@nimit9/signet-ui/components/button"
import { useMediaQuery } from "@nimit9/signet-ui/hooks/use-media-query"
import { createFileRoute, Link, Outlet, useRouterState } from "@tanstack/react-router"
import { ChevronLeft } from "lucide-react"
import { Header } from "@/components/layout/header"
import { cn } from "@/lib/cn"
import { findSettingsSection, SETTINGS_NAV } from "./_nav"

export const Route = createFileRoute("/settings")({
  component: SettingsLayout,
})

/**
 * Desktop (md+): left section nav + content pane, as before.
 * Phone: no nav chrome at all — /settings itself is the grouped list (rendered
 * by the index route), and every sub-route gets a back link to it plus its
 * title. Nothing scrolls sideways at 390px.
 */
function SettingsLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const isDesktop = useMediaQuery("(min-width: 768px)")
  const section = findSettingsSection(pathname)

  if (!isDesktop) {
    return (
      <>
        <Header title={section?.label ?? "Settings"} showPeriod={false} />
        <main
          id="main-content"
          tabIndex={-1}
          className="flex-1 overflow-x-hidden overflow-y-auto px-4 py-4 outline-none"
        >
          {section && (
            <div className="mb-5 space-y-2 border-b border-border-subtle pb-4">
              <Link
                to="/settings"
                className={cn(buttonVariants({ variant: "ghost", size: "sm" }), "-ml-2")}
              >
                <ChevronLeft />
                Settings
              </Link>
              <div>
                <h2 className="text-title font-semibold tracking-tight text-text-primary">
                  {section.label}
                </h2>
                <p className="mt-1 text-body text-text-secondary">{section.subtitle}</p>
              </div>
            </div>
          )}
          <Outlet />
        </main>
      </>
    )
  }

  return (
    <>
      <Header
        title="Settings"
        subtitle={`Settings / ${section?.label ?? "Settings"}`}
        showPeriod={false}
      />

      <div className="flex flex-1 overflow-hidden">
        <nav
          aria-label="Settings sections"
          data-testid="settings-desktop-nav"
          className="flex w-52 shrink-0 flex-col gap-4 overflow-y-auto border-r border-border-subtle px-3 py-5 lg:w-56"
        >
          {SETTINGS_NAV.map((group) => (
            <div key={group.label}>
              <p className="mb-1 px-3 text-label font-semibold tracking-wide text-text-hint">
                {group.label}
              </p>
              <div className="flex flex-col gap-0.5">
                {group.items.map((item) => {
                  const Icon = item.icon
                  const isActive = pathname.startsWith(item.to)
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      aria-current={isActive ? "page" : undefined}
                      className={cn(
                        "flex items-center gap-2.5 rounded-md px-3 py-2 text-body transition-colors",
                        isActive
                          ? "bg-accent-green/10 font-medium text-accent-green"
                          : "text-text-secondary hover:bg-bg-elevated hover:text-text-primary",
                      )}
                    >
                      <Icon
                        className={cn(
                          "h-4 w-4 shrink-0",
                          isActive ? "text-accent-green" : "text-text-muted",
                        )}
                      />
                      {item.label}
                    </Link>
                  )
                })}
              </div>
            </div>
          ))}
        </nav>

        <main
          id="main-content"
          tabIndex={-1}
          className="min-w-0 flex-1 overflow-y-auto p-5 outline-none lg:p-8"
        >
          {section && (
            <div className="mb-6 border-b border-border-subtle pb-4">
              <h2 className="text-title font-semibold tracking-tight text-text-primary">
                {section.label}
              </h2>
              <p className="mt-1 text-body text-text-secondary">{section.subtitle}</p>
            </div>
          )}

          <Outlet />
        </main>
      </div>
    </>
  )
}
